import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { PublicKey } from '@solana/web3.js';
import { useQuery } from '@tanstack/react-query';
import { erc20Abi } from 'viem';
import { useAccount, useBalance, usePublicClient, useReadContracts } from 'wagmi';
import {
  parseEvmChainId,
  type AnyChainId,
  isSolanaChain,
  isTronChain,
  type AnyChainData,
} from '@/shared/assets/chains';
import { NATIVE_TOKEN_ADDRESS } from '@/shared/assets/tokens/constants';
import { useTronWallet } from '@/shared/components/TronConnect';
import { useTokens } from '@/shared/hooks/useTokens';
import { getChainflipToken } from '@/shared/utils/env';
import TokenAmount from '@/shared/utils/TokenAmount';

interface BalancesData {
  balances: Partial<Record<string, TokenAmount>>;
  isLoading: boolean;
}

type Balances = Partial<Record<AnyChainId, Partial<Record<string, TokenAmount>>>> & {
  evmAddress?: string;
  solAddress?: string;
  tronAddress?: string;
};

type ContextValue = [Balances, Dispatch<SetStateAction<Balances>>];

const Context = createContext<ContextValue | null>(null);

export const ChainBalancesProvider = ({ children }: { children: ReactNode }) => {
  const [balances, setBalances] = useState<Balances>({});

  const ctx = useMemo(() => [balances, setBalances] as ContextValue, [balances]);

  return <Context.Provider value={ctx}>{children}</Context.Provider>;
};

export const useChainBalances = (chain: AnyChainData | undefined): BalancesData => {
  const ctx = useContext(Context);

  if (ctx === null) {
    throw new Error('useChainBalances must be used within a ChainBalancesProvider');
  }

  const [balances, setBalances] = ctx;

  const { address: connectedAddress } = useAccount();
  const solanaWallet = useWallet();
  const { connection: solanaConnection } = useConnection();
  const tronWallet = useTronWallet();

  const { tokens, isLoading: tokensLoading } = useTokens(chain?.id);
  const isChainTron = isTronChain(chain?.id);
  // Tron chains are handled via TronLink, not wagmi — skip wagmi path for them
  const evmChainId = isChainTron ? undefined : parseEvmChainId(chain?.id);
  const publicClient = usePublicClient({ chainId: evmChainId });

  const { data: solBalance, isLoading: solBalanceLoading } = useQuery({
    queryKey: ['SOL_BALANCE', solanaWallet.publicKey],
    enabled: solanaWallet.connected,
    queryFn: async () => {
      const balance = await solanaConnection.getBalance(solanaWallet.publicKey!);
      return TokenAmount.fromAsset(balance, 'Sol');
    },
  });

  const solUsdcAddress = getChainflipToken('SolUsdc').address;

  const { data: solUsdcBalance, isLoading: solUsdcBalanceLoading } = useQuery({
    queryKey: ['SOL_USDC_BALANCE', solanaWallet.publicKey],
    enabled: solanaWallet.connected,
    queryFn: async () => {
      const accounts = await solanaConnection.getTokenAccountsByOwner(solanaWallet.publicKey!, {
        mint: new PublicKey(solUsdcAddress),
      });
      const account = accounts.value.at(0);
      if (!account) return undefined;
      const balance = await solanaConnection.getTokenAccountBalance(account.pubkey);
      return TokenAmount.fromAsset(balance.value.amount, 'SolUsdc');
    },
  });

  useEffect(() => {
    const chainId = chain?.id;
    const isSolana = isSolanaChain(chainId);

    if (!isSolana) return;

    setBalances((current) => {
      const solAddress = solanaWallet.publicKey?.toString();

      return {
        ...current,
        solAddress,
        [chainId]: { [NATIVE_TOKEN_ADDRESS]: solBalance, [solUsdcAddress]: solUsdcBalance },
      };
    });
  }, [solBalance, solUsdcBalance, solanaWallet.publicKey, chain?.id]);

  const calls = useMemo(() => {
    if (!connectedAddress || !publicClient?.chain || evmChainId === undefined) {
      return undefined;
    }

    const { id: chainId } = publicClient.chain;
    if (evmChainId !== chainId) return undefined;

    return tokens
      .filter((token) => token.address !== NATIVE_TOKEN_ADDRESS)
      .map(
        (token) =>
          ({
            chainId: evmChainId,
            abi: erc20Abi,
            address: token.address as `0x${string}`,
            functionName: 'balanceOf',
            args: [connectedAddress],
            token,
          }) as const,
      );
  }, [tokens, publicClient, connectedAddress, evmChainId]);

  const { isLoading: tokenBalancesLoading, data: tokenBalancesData } = useReadContracts({
    contracts: calls,
    allowFailure: true,
    query: {
      refetchInterval: 10_000,
    },
  });

  const { isLoading: nativeBalanceLoading, data: nativeBalance } = useBalance({
    address: connectedAddress,
    chainId: evmChainId,
    query: {
      enabled: evmChainId !== undefined,
      refetchInterval: 10_000,
    },
  });

  const trxUsdtAddress = getChainflipToken('TrxUsdt').address;

  const { isLoading: trxBalanceLoading, data: trxBalance } = useQuery({
    queryKey: ['TRX_BALANCE', tronWallet.address],
    enabled: isChainTron && tronWallet.isConnected,
    queryFn: async () => {
      const balance = await window.tronWeb?.trx.getBalance(tronWallet.address!);
      if (!balance) return undefined;
      return new TokenAmount(String(balance), 6);
    },
    refetchInterval: 10_000,
  });

  const trc20BalanceOfAbi = [
    {
      constant: true,
      inputs: [{ name: '_owner', type: 'address' }],
      name: 'balanceOf',
      outputs: [{ name: 'balance', type: 'uint256' }],
      type: 'Function',
    },
  ];

  const { isLoading: trxUsdtBalanceLoading, data: trxUsdtBalance } = useQuery({
    queryKey: ['TRX_USDT_BALANCE', tronWallet.address],
    enabled: isChainTron && tronWallet.isConnected,
    queryFn: async () => {
      const contract = window.tronWeb?.contract(trc20BalanceOfAbi, trxUsdtAddress);
      const result = await contract?.balanceOf(tronWallet.address!).call();
      return result ? new TokenAmount(result.toString(), 6) : undefined;
    },
    refetchInterval: 10_000,
  });

  useEffect(() => {
    if (!chain?.id || !tokenBalancesData || !nativeBalance) return;

    const balanceEntries = [
      [NATIVE_TOKEN_ADDRESS, new TokenAmount(String(nativeBalance.value), nativeBalance.decimals)],
      ...tokenBalancesData.map((result, index) => [
        calls?.[index].address,
        new TokenAmount(String(result.result ?? 0), calls?.[index].token.decimals),
      ]),
    ];
    if (!balanceEntries.length) return;

    setBalances((current) => {
      if (current.evmAddress !== connectedAddress) {
        return { evmAddress: connectedAddress, [chain.id]: Object.fromEntries(balanceEntries) };
      }

      return { ...current, [chain.id]: Object.fromEntries(balanceEntries) };
    });
  }, [tokenBalancesData, nativeBalance, tokens, chain?.id]);

  useEffect(() => {
    const chainId = chain?.id;
    if (!isTronChain(chainId)) return;

    setBalances((current) => ({
      ...current,
      tronAddress: tronWallet.address ?? undefined,
      [chainId]: {
        [NATIVE_TOKEN_ADDRESS]: trxBalance,
        [trxUsdtAddress]: trxUsdtBalance,
      },
    }));
  }, [trxBalance, trxUsdtBalance, tronWallet.address, chain?.id]);

  return useMemo(
    () => ({
      balances: (chain?.id && balances[chain.id]) ?? {},
      isLoading:
        tokensLoading ||
        tokenBalancesLoading ||
        nativeBalanceLoading ||
        solBalanceLoading ||
        solUsdcBalanceLoading ||
        trxBalanceLoading ||
        trxUsdtBalanceLoading,
    }),
    [
      balances,
      tokenBalancesLoading,
      nativeBalanceLoading,
      tokensLoading,
      solBalanceLoading,
      trxBalanceLoading,
      trxUsdtBalanceLoading,
      chain?.id,
    ],
  );
};
